/**
 * VoucherAdminPage · v2.7.65+E132 (2026-02-XX)
 * ═════════════════════════════════════════════════════════════════════
 * Admin console for the Community Voucher Program.
 *
 * RBAC:
 *   L5 (super_user + general_partner) — full CRUD (approve/reject, assign serials, void)
 *   Dispatcher — dispatch-lookup only (falls back to a compact search-only shell)
 *
 * Layout:
 *   ┌────────────────┬───────────────────────────────────────┐
 *   │ Applications   │  Detail pane (form/docs/vouchers)    │
 *   │ (filter+list)  │                                      │
 *   └────────────────┴───────────────────────────────────────┘
 *   Dispatch Quick-Lookup card at the top (visible for both roles).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, Lock, Search, FileText, CheckCircle2, XCircle,
  ShieldCheck, Users, Plus, Ban, ExternalLink, ClipboardList,
  Phone, MapPin, RefreshCw, Ticket, Calendar, Clock, DollarSign,
  AlertTriangle, ScanLine,
} from "lucide-react";

const STATUS_STYLES = {
  pending:  { color: "#F59E0B", fill: "rgba(245,158,11,0.15)" },
  approved: { color: "#10B981", fill: "rgba(16,185,129,0.15)" },
  rejected: { color: "#EF4444", fill: "rgba(239,68,68,0.15)" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/* ─────────────────────── Quick-Stats Bar (v2.7.65+E137) ────────── */
function QuickStatsBar() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/vouchers/quick-stats");
      setStats(data);
    } catch (_e) {
      // Non-authorized roles hit 403 — silently render zeros.
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 90_000);
    return () => clearInterval(id);
  }, [load]);

  const tiles = [
    { key: "approved_riders",       label: "Approved Riders",    Icon: ShieldCheck, tint: "emerald" },
    { key: "pending_applications",  label: "Pending Applications", Icon: ClipboardList, tint: "amber" },
    { key: "issued_serials",        label: "Active Serials",     Icon: Ticket,      tint: "fuchsia" },
    { key: "active_rides",          label: "Active CCVAP Rides", Icon: Search,      tint: "sky" },
  ];
  const tintClasses = {
    emerald: "border-emerald-400/40 bg-emerald-500/10 text-emerald-300",
    amber:   "border-amber-400/40   bg-amber-500/10   text-amber-300",
    fuchsia: "border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-300",
    sky:     "border-sky-400/40     bg-sky-500/10     text-sky-300",
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="voucher-quick-stats">
      {tiles.map(({ key, label, Icon, tint }) => (
        <div key={key} className={`rounded-lg border-2 ${tintClasses[tint]} p-3`} data-testid={`voucher-stat-${key}`}>
          <div className="flex items-center justify-between mb-1">
            <Icon className="h-4 w-4 opacity-80"/>
            <div className="text-2xl font-black leading-none">
              {loading ? "…" : (stats?.[key] ?? 0)}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-black opacity-90 mt-1">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ─────────────────────── Serial Verifier (v2.7.65+E137) ────────── */
function SerialVerifier() {
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const s = serial.trim();
    if (!s) { toast.error("Enter a voucher serial number"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await adminApi.get(`/admin/vouchers/verify?serial=${encodeURIComponent(s)}`);
      setResult(data);
      if (!data.found) toast.info("Serial not on file — see details below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const statusPill = (status) => {
    const map = {
      issued:  { color: "text-emerald-300", bg: "bg-emerald-500/20 border-emerald-400/50", label: "ACTIVE" },
      used:    { color: "text-zinc-300",    bg: "bg-zinc-700/40 border-zinc-500/40",       label: "USED" },
      voided:  { color: "text-rose-300",    bg: "bg-rose-500/20 border-rose-400/50",       label: "VOIDED" },
      expired: { color: "text-amber-300",   bg: "bg-amber-500/20 border-amber-400/50",     label: "EXPIRED" },
    }[status] || { color: "text-zinc-300", bg: "bg-zinc-700/40 border-zinc-500/40", label: (status || "?").toUpperCase() };
    return (
      <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${map.bg} ${map.color}`} data-testid="verify-status-pill">
        {map.label}
      </span>
    );
  };

  return (
    <div
      className="rounded-xl border-2 border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-500/10 to-amber-500/5 p-5 mb-4 shadow-lg"
      data-testid="serial-verifier-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <ScanLine className="h-5 w-5 text-fuchsia-300"/>
        <h2 className="text-sm font-black text-fuchsia-200 uppercase tracking-wider">
          Serial Number Verification
        </h2>
        <span className="text-[10px] text-zinc-400 hidden sm:inline">
          verify one CCVAP voucher before dispatch — full status, balance, expiry, usage log
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="e.g. BT-VCH-000123"
          data-testid="verify-serial-input"
          className="flex-1 bg-black/40 border-fuchsia-500/40 text-white font-mono"
        />
        <Button
          onClick={run}
          disabled={busy}
          data-testid="verify-serial-btn"
          className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-bold"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <ScanLine className="h-4 w-4 mr-1"/>}
          Verify
        </Button>
      </div>
      {result && !result.found && (
        <div className="rounded-lg border border-rose-400/50 bg-rose-500/10 p-3 flex items-start gap-2" data-testid="verify-not-found">
          <XCircle className="h-5 w-5 text-rose-300 flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold text-rose-200">Serial not on file</div>
            <div className="text-xs text-rose-100/80 mt-0.5">{result.message} · Double-check the number and try again, or search by rider name in the Dispatch Quick-Lookup below.</div>
          </div>
        </div>
      )}
      {result && result.found && (
        <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/5 p-3 space-y-3" data-testid="verify-hit">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="font-mono text-sm font-black text-white">{result.voucher_serial_number}</div>
              <div className="text-xs text-zinc-400 mt-0.5">Rider · <span className="text-emerald-300 font-bold">{result.rider_name || "—"}</span></div>
            </div>
            <div className="flex items-center gap-2">
              {statusPill(result.status)}
              {result.days_remaining !== null && result.status === "issued" && (
                <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${
                  result.days_remaining <= 14
                    ? "bg-amber-500/20 border-amber-400/60 text-amber-300"
                    : "bg-zinc-800 border-zinc-700 text-zinc-300"
                }`} data-testid="verify-countdown">
                  <Clock className="h-3 w-3 inline mr-0.5"/>{result.days_remaining}d LEFT
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="rounded bg-black/30 p-2">
              <div className="text-[9px] uppercase text-zinc-500 tracking-widest font-bold">Balance</div>
              <div className="font-black text-emerald-300 text-base flex items-center gap-1" data-testid="verify-balance">
                <DollarSign className="h-3 w-3"/>{(result.remaining_balance_usd || 0).toFixed(2)}
              </div>
            </div>
            <div className="rounded bg-black/30 p-2">
              <div className="text-[9px] uppercase text-zinc-500 tracking-widest font-bold">Allocated To</div>
              <div className="text-zinc-100 flex items-center gap-1">
                {result.allocated_to_type === "child" ? <Users className="h-3 w-3"/> : <ShieldCheck className="h-3 w-3"/>}
                <span className="truncate">{result.allocated_to_name}</span>
              </div>
            </div>
            <div className="rounded bg-black/30 p-2">
              <div className="text-[9px] uppercase text-zinc-500 tracking-widest font-bold">Assigned</div>
              <div className="text-zinc-100 flex items-center gap-1">
                <Calendar className="h-3 w-3"/>{fmt(result.assigned_at).split(",")[0]}
              </div>
            </div>
            <div className="rounded bg-black/30 p-2">
              <div className="text-[9px] uppercase text-zinc-500 tracking-widest font-bold">Expires</div>
              <div className="text-zinc-100 flex items-center gap-1">
                <Calendar className="h-3 w-3"/>{fmt(result.expires_at).split(",")[0]}
              </div>
            </div>
          </div>
          {result.rider_phone && (
            <div className="text-xs text-zinc-300 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-zinc-500"/>{result.rider_phone}</span>
              {result.rider_email && <span className="text-zinc-500">{result.rider_email}</span>}
            </div>
          )}
          {result.usage_count > 0 && (
            <div className="pt-2 border-t border-emerald-400/20">
              <div className="text-[10px] uppercase text-zinc-400 tracking-widest font-bold mb-1">
                Usage Log · {result.usage_count} trip{result.usage_count === 1 ? "" : "s"}
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {result.usage_log.map((t) => (
                  <div key={t.trip_id} className="flex items-center justify-between rounded bg-black/40 px-2 py-1 text-[11px] font-mono" data-testid={`verify-trip-${t.trip_id}`}>
                    <span className="text-zinc-300">{t.trip_id}</span>
                    <span className="text-zinc-500">{t.status}</span>
                    <span className="text-emerald-300">${(t.fare_usd || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────── Dispatch Quick-Lookup ─────────────────── */
function DispatchLookup() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const run = async () => {
    if (q.trim().length < 2) {
      toast.error("Enter at least 2 characters to search");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.get(
        `/admin/vouchers/dispatch-lookup?q=${encodeURIComponent(q.trim())}`
      );
      setResults(data?.results || []);
      if ((data?.count ?? 0) === 0) toast.info("No matching approved riders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="dispatch-lookup-card"
      className="rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 to-rose-500/10 p-5 mb-6 shadow-lg"
    >
      <div className="flex items-center gap-3 mb-3">
        <Search className="h-5 w-5 text-amber-400" />
        <h2 className="text-sm font-black text-amber-300 uppercase tracking-wider">
          Dispatch Quick-Lookup
        </h2>
        <span className="text-[10px] text-zinc-400 hidden sm:inline">
          verify approved riders + voucher serial numbers during booking calls
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="Name · phone · email · voucher serial number…"
          data-testid="dispatch-lookup-input"
          className="flex-1 bg-black/40 border-amber-500/40 text-white"
        />
        <Button
          onClick={run}
          disabled={busy}
          data-testid="dispatch-lookup-search"
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
          Search
        </Button>
      </div>
      {results.length > 0 && (
        <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="dispatch-lookup-results">
          {results.map((r) => (
            <li key={r.application_id}
                data-testid={`dispatch-hit-${r.application_id}`}
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-bold text-emerald-200 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {r.rider_name}
                </div>
                <span className="text-xs text-zinc-400">{r.city}, {r.state}</span>
              </div>
              <div className="text-xs text-zinc-300 flex flex-wrap items-center gap-3 mb-2">
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>
                <span>·</span>
                <span>{r.email}</span>
              </div>
              {r.active_vouchers.length === 0 ? (
                <div className="text-xs text-amber-300/80 italic">No active issued vouchers</div>
              ) : (
                <div className="mt-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                    Active Voucher Serials
                  </div>
                  {r.active_vouchers.map((v) => (
                    <div key={v.id}
                         data-testid={`dispatch-hit-voucher-${v.id}`}
                         className="flex items-center gap-2 rounded bg-black/40 px-2 py-1 font-mono text-xs">
                      <Ticket className="h-3 w-3 text-amber-400" />
                      <span className="text-amber-200 font-bold">{v.voucher_serial_number}</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-zinc-300 uppercase tracking-wider">
                        {v.allocated_to_type}
                        {v.dependent_name && ` · ${v.dependent_name}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────── Applications list ─────────────────────── */
function ApplicationsList({ selectedId, onSelect, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      const { data } = await adminApi.get(`/admin/vouchers/applications?${params.toString()}`);
      setRows(data?.applications || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4" data-testid="voucher-applications-list">
      <h2 className="text-sm font-black text-amber-300 uppercase tracking-wider mb-3 flex items-center gap-2">
        <ClipboardList className="h-4 w-4" />Applications ({rows.length})
      </h2>
      <div className="flex gap-2 mb-3">
        <Input placeholder="Search name · phone · email…" value={q}
               onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && load()}
               data-testid="voucher-app-search"
               className="bg-black/40 border-white/15 text-white text-sm" />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="voucher-app-status-filter"
          className="bg-black/40 border border-white/15 rounded-md px-2 py-1 text-sm text-white"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                data-testid="voucher-app-refresh" className="text-zinc-300">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <ul className="space-y-2 max-h-[600px] overflow-y-auto">
        {rows.map((a) => {
          const s = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
          return (
            <li key={a.id}>
              <button
                onClick={() => onSelect(a.id)}
                data-testid={`voucher-app-row-${a.id}`}
                className={`w-full text-left rounded-lg border px-3 py-2 transition ${
                  selectedId === a.id
                    ? "border-amber-400 bg-amber-500/10"
                    : "border-white/10 hover:border-amber-500/40 hover:bg-white/5"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-sm text-white">
                    {a.first_name} {a.last_name}
                  </span>
                  <span
                    className="text-[9px] uppercase font-black tracking-widest px-1.5 py-0.5 rounded border"
                    style={{ color: s.color, borderColor: s.color, backgroundColor: s.fill }}
                  >{a.status}</span>
                </div>
                <div className="text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
                  <span>{a.phone}</span><span>·</span>
                  <span className="truncate">{a.email}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {a.city}, {a.state} {a.zip_code} · {fmt(a.created_at)}
                </div>
              </button>
            </li>
          );
        })}
        {rows.length === 0 && !loading && (
          <li className="text-sm text-zinc-500 py-8 text-center" data-testid="voucher-app-empty">
            No applications yet.
          </li>
        )}
      </ul>
    </div>
  );
}

/* ─────────────────────── Application Detail ─────────────────────── */
function ApplicationDetail({ appId, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [serialInput, setSerialInput] = useState("");
  const [allocateTo, setAllocateTo] = useState("adult");
  const [dependentId, setDependentId] = useState("");

  const load = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/vouchers/applications/${appId}`);
      setDetail(data);
      setNotes(data.review_notes || "");
    } catch { setDetail(null); }
    finally { setLoading(false); }
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try {
      await adminApi.post(`/admin/vouchers/applications/${appId}/status`, { status, notes });
      toast.success(`Application ${status}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const assignSerial = async () => {
    if (!serialInput.trim()) {
      toast.error("Enter a voucher serial number");
      return;
    }
    if (allocateTo === "child" && !dependentId) {
      toast.error("Select which dependent child this voucher is for");
      return;
    }
    try {
      await adminApi.post(`/admin/vouchers/applications/${appId}/vouchers`, {
        voucher_serial_number: serialInput.trim(),
        allocated_to_type:     allocateTo,
        dependent_id:          allocateTo === "child" ? dependentId : null,
      });
      toast.success(`Serial ${serialInput} assigned`);
      setSerialInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    }
  };

  const voidVoucher = async (vid) => {
    if (!window.confirm("Void this voucher? It won't be usable at pickup.")) return;
    try {
      await adminApi.patch(`/admin/vouchers/rider-vouchers/${vid}`, { status: "voided" });
      toast.success("Voucher voided");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    }
  };

  if (!appId) {
    return (
      <div className="rounded-xl border border-dashed border-white/20 p-8 text-center text-sm text-zinc-500" data-testid="voucher-detail-empty">
        Select an application from the list.
      </div>
    );
  }
  if (loading || !detail) {
    return (
      <div className="rounded-xl border border-white/10 p-8 text-center text-zinc-400" data-testid="voucher-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />Loading…
      </div>
    );
  }

  const s = STATUS_STYLES[detail.status];
  return (
    <div className="rounded-xl border border-amber-500/25 bg-zinc-900/60 p-5" data-testid={`voucher-detail-${appId}`}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-3 border-b border-white/10">
        <div>
          <h2 className="text-xl font-black text-white uppercase tracking-tight">
            {detail.first_name} {detail.middle_name} {detail.last_name}
          </h2>
          <div className="text-xs text-zinc-400 mt-1 flex flex-wrap items-center gap-2">
            <span>{detail.phone}</span><span>·</span>
            <span>{detail.email}</span><span>·</span>
            <span>{detail.address}, {detail.city}, {detail.state} {detail.zip_code}</span>
          </div>
        </div>
        <span
          className="uppercase font-black tracking-widest text-xs px-3 py-1.5 rounded-full border-2"
          style={{ color: s.color, borderColor: s.color, backgroundColor: s.fill }}
          data-testid="voucher-detail-status"
        >{detail.status}</span>
      </div>

      {/* Documents */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <a href={detail.id_document_url} target="_blank" rel="noopener noreferrer"
           data-testid="voucher-detail-id-doc"
           className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 hover:bg-amber-500/10 transition">
          <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" />ID Document
          </div>
          <div className="text-sm text-white truncate">{detail.id_document_name || "View file"}</div>
          <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
            Open <ExternalLink className="h-3 w-3" />
          </div>
        </a>
        <a href={detail.verification_document_url} target="_blank" rel="noopener noreferrer"
           data-testid="voucher-detail-verif-doc"
           className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 hover:bg-rose-500/10 transition">
          <div className="text-[10px] uppercase tracking-wider text-rose-400 font-bold mb-1 flex items-center gap-1">
            <FileText className="h-3 w-3" />Assistance Verification
          </div>
          <div className="text-sm text-white truncate">{detail.verification_document_name || "View file"}</div>
          <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
            Open <ExternalLink className="h-3 w-3" />
          </div>
        </a>
      </div>

      {/* Programs */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          Assistance Programs
        </div>
        <div className="flex flex-wrap gap-1.5">
          {detail.assistance_programs.map((p) => (
            <span key={p} data-testid={`voucher-detail-program-${p.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40">{p}</span>
          ))}
        </div>
      </div>

      {/* Dependents */}
      {detail.dependents.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1 flex items-center gap-1">
            <Users className="h-3 w-3" />Dependent Children
          </div>
          <ul className="space-y-1">
            {detail.dependents.map((d) => (
              <li key={d.id}
                  data-testid={`voucher-detail-dep-${d.id}`}
                  className="text-sm rounded border border-white/10 bg-black/20 px-2 py-1 flex items-center justify-between">
                <span>{d.first_name} {d.middle_name} {d.last_name}</span>
                <span className="text-xs text-zinc-500 font-mono">DOB {d.dob}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Review actions */}
      <div className="rounded-lg border border-white/10 bg-black/30 p-3 mb-4">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">
          Review Notes (optional)
        </label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Reason for approval/rejection…"
          data-testid="voucher-detail-notes"
          className="bg-black/40 border-white/15 text-white text-sm mb-2"
        />
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            disabled={detail.status === "approved"}
            onClick={() => setStatus("approved")}
            data-testid="voucher-detail-approve"
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />Approve
          </Button>
          <Button
            size="sm"
            disabled={detail.status === "rejected"}
            onClick={() => setStatus("rejected")}
            data-testid="voucher-detail-reject"
            className="bg-rose-600 hover:bg-rose-500 text-white font-bold"
          >
            <XCircle className="h-4 w-4 mr-1" />Reject
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={detail.status === "pending"}
            onClick={() => setStatus("pending")}
            data-testid="voucher-detail-reset"
            className="border-amber-500 text-amber-300"
          >
            Reset to Pending
          </Button>
        </div>
        {detail.reviewed_by && (
          <div className="text-[11px] text-zinc-500 mt-2">
            Last reviewed by <span className="text-zinc-300">{detail.reviewed_by}</span> at {fmt(detail.reviewed_at)}
          </div>
        )}
      </div>

      {/* Voucher serial management (only when approved) */}
      {detail.status === "approved" && (
        <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-3" data-testid="voucher-serial-manager">
          <h3 className="text-sm font-black text-amber-300 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Ticket className="h-4 w-4" />Voucher Serial Assignment
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
            <Input
              placeholder="Serial number (e.g. BT-0042)"
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
              data-testid="voucher-serial-input"
              className="bg-black/40 border-amber-500/30 text-white text-sm sm:col-span-2"
            />
            <select
              value={allocateTo}
              onChange={(e) => setAllocateTo(e.target.value)}
              data-testid="voucher-serial-alloc"
              className="bg-black/40 border border-amber-500/30 rounded-md px-2 py-1 text-sm text-white"
            >
              <option value="adult">Adult (primary)</option>
              <option value="child">Child</option>
            </select>
            <Button
              onClick={assignSerial}
              data-testid="voucher-serial-assign"
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
            >
              <Plus className="h-4 w-4 mr-1" />Assign
            </Button>
          </div>
          {allocateTo === "child" && detail.dependents.length > 0 && (
            <select
              value={dependentId}
              onChange={(e) => setDependentId(e.target.value)}
              data-testid="voucher-serial-dep-select"
              className="w-full mb-3 bg-black/40 border border-amber-500/30 rounded-md px-2 py-1.5 text-sm text-white"
            >
              <option value="">Select the named child…</option>
              {detail.dependents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.first_name} {d.last_name} (DOB {d.dob})
                </option>
              ))}
            </select>
          )}

          {detail.vouchers.length === 0 && (
            <div className="text-xs text-zinc-400 italic">No voucher serials assigned yet.</div>
          )}
          {detail.vouchers.length > 0 && (
            <ul className="space-y-1" data-testid="voucher-serial-list">
              {detail.vouchers.map((v) => (
                <li key={v.id}
                    data-testid={`voucher-serial-row-${v.id}`}
                    className="flex items-center justify-between gap-2 rounded bg-black/40 border border-white/10 px-3 py-1.5 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Ticket className="h-4 w-4 text-amber-400 flex-shrink-0" />
                    <span className="font-mono font-bold text-amber-200">{v.voucher_serial_number}</span>
                    <span className="text-zinc-500 hidden sm:inline">·</span>
                    <span className="text-xs uppercase tracking-wider text-zinc-300 truncate">
                      {v.allocated_to_type}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ml-1"
                      style={{
                        color: v.status === "voided" ? "#ef4444" : v.status === "used" ? "#a1a1aa" : "#10b981",
                        border: `1px solid ${v.status === "voided" ? "#ef4444" : v.status === "used" ? "#a1a1aa" : "#10b981"}`,
                      }}
                    >{v.status}</span>
                  </div>
                  {v.status === "issued" && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => voidVoucher(v.id)}
                      data-testid={`voucher-serial-void-${v.id}`}
                      className="text-rose-400 hover:text-rose-300 h-7"
                    >
                      <Ban className="h-3.5 w-3.5 mr-1" />Void
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Page shell ─────────────────────── */
export default function VoucherAdminPage() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [denied, setDenied] = useState(false);
  const [dispatchOnly, setDispatchOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // L5 endpoint — 200 for L5, 403 for dispatch, 401 unauth
        await adminApi.get("/admin/vouchers/applications?status=pending");
        if (!cancelled) { setDenied(false); setDispatchOnly(false); }
      } catch (e) {
        if (e?.response?.status === 403) {
          // Might be a dispatcher — check dispatch endpoint
          try {
            await adminApi.get("/admin/vouchers/dispatch-lookup?q=aa");
            if (!cancelled) setDispatchOnly(true);
          } catch (_e2) {
            if (!cancelled) setDenied(true);
          }
        } else if (!cancelled) {
          setDenied(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300" data-testid="voucher-admin-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />Checking access…
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-200 p-6" data-testid="voucher-admin-denied">
        <Lock className="h-12 w-12 text-rose-400 mb-3" />
        <h1 className="text-xl font-bold mb-1">Access Denied</h1>
        <p className="text-sm text-zinc-400 mb-4 max-w-md text-center">
          Voucher admin is restricted to super_user, general_partner, or dispatcher accounts.
        </p>
        <Button variant="outline" onClick={() => navigate("/admin")} data-testid="voucher-admin-denied-back">
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Admin Console
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 sm:px-6 lg:px-10 py-6" data-testid="voucher-admin-root">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}
                data-testid="voucher-admin-back"
                className="mb-3 -ml-2 text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Admin Console
        </Button>
        <div className="flex items-start gap-3 mb-6">
          <div className="rounded-xl bg-amber-500 text-black p-2.5"><ShieldCheck className="h-7 w-7" /></div>
          <div>
            <h1 className="text-3xl font-black text-amber-300 uppercase tracking-tight" data-testid="voucher-admin-title">
              Voucher Program {dispatchOnly ? "Lookup" : "Admin"}
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              {dispatchOnly
                ? "Dispatcher read-only view — verify approved riders + voucher serial numbers during booking calls."
                : "Review applications, manage voucher serial assignments, and run the dispatch lookup."}
            </p>
          </div>
        </div>

        <QuickStatsBar />
        <SerialVerifier />
        <DispatchLookup />

        {!dispatchOnly && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <ApplicationsList
                selectedId={selectedId}
                onSelect={setSelectedId}
                refreshKey={refreshKey}
              />
            </div>
            <div className="lg:col-span-2">
              <ApplicationDetail
                appId={selectedId}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
